// v237 SeedBanner — strip rendered above the topbar in AppShell /
// AdminShell when /api/public-config returns seed_active=true. The
// `orbital seed` CLI subcommand (item 67 in the recommended delivery
// order of PRODUCT-ROADMAP.md) populates the OrbitalReg single-row
// state and the AppShell tells eval / sales-demo customers they are
// looking at injected demo data, not at production records.
//
// Dismissible per-set in localStorage so re-applying with a different
// set re-shows the strip; non-dismissable while seed_active is on
// would be too aggressive for a sales demo where the engineer wants
// to free the topbar for a screenshot.

import { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';

const DISMISSED_KEY = 'orbital.seed.banner.dismissed';

export function SeedBanner({ setName }: Readonly<{ setName?: string }>) {
  const [dismissed, setDismissed] = useState(false);
  const key = (setName ?? 'default').trim() || 'default';

  // Re-evaluate dismissal whenever the active set changes — switching
  // sets is a deliberate "show me the new shape" action; the banner
  // should reappear so the user can confirm what was applied.
  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISSED_KEY + '.' + key) === '1');
    } catch {
      setDismissed(false);
    }
  }, [key]);

  if (dismissed) return null;

  return (
    <div className="border-b border-accent/30 bg-accent/10">
      <div className="px-6 py-2 flex items-start gap-3 text-sm">
        <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-accent" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-fg">Demo data active</span>
          <span className="text-fg-muted ml-2 whitespace-pre-wrap break-words">
            This deployment was populated by{' '}
            <code className="px-1 rounded bg-bg-elevated/80">orbital seed</code>
            {setName ? ` (${setName} set)` : ''}. Run{' '}
            <code className="px-1 rounded bg-bg-elevated/80">orbital seed --reset</code>{' '}
            to clear before going live.
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            try {
              localStorage.setItem(DISMISSED_KEY + '.' + key, '1');
            } catch {
              /* localStorage disabled — banner just won't stay dismissed */
            }
            setDismissed(true);
          }}
          className="text-fg-muted hover:text-fg transition-colors shrink-0"
          aria-label="Dismiss demo-data banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
